import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, MoonStar, CalendarCheck2, AlertTriangle, PlayCircle, CheckCircle2, History, IndianRupee, User, Clock } from "lucide-react";
import api, { errMsg } from "@/lib/api";

/* Night Audit — per-property Operating Day machinery.
   Auto-run whenever the day is fully paid & past the cutoff (03:00 by default).
   Manual "Run Night Audit" is available in both Pending and Held states. */
function fmtMonth(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { month: "long", year: "numeric" });
}

export default function NightAuditTab({ pid, propertyName }) {
  const [d, setD] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);

  const load = useCallback(() => {
    setD(null);
    api.get(`/pms/properties/${pid}/night-audit`).then(({ data }) => setD(data))
      .catch((e) => toast.error(errMsg(e)));
    api.get(`/pms/properties/${pid}/night-audit/pending-month`).then(({ data }) => setPending(data))
      .catch(() => setPending(null));
  }, [pid]);
  useEffect(() => { load(); }, [load]);

  const runAudit = async (force = false, notes = "") => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/pms/properties/${pid}/night-audit/run`, { force, notes });
      toast.success(`Operating Day ${r.closed} closed · next day is ${r.next_operating_day}${r.forced ? " · closed under force with unpaid balance" : ""}`);
      setForceOpen(false);
      load();
    } catch (e) {
      // Expected 400 with blockers list when force=false
      const detail = e?.response?.data?.detail;
      if (detail?.blockers) {
        setForceOpen(true);
        toast.error(detail.message || "Cannot close — outstanding payments");
      } else {
        toast.error(errMsg(e));
      }
    }
    setBusy(false);
  };

  if (!d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;
  const c = d.current || {};
  const held = c.status === "held";
  const canRun = c.status === "pending" || c.status === "held";
  const blockers = c.blockers || [];
  const isBehind = c.operating_day < d.business_today;

  return (
    <div className="reveal space-y-4">
      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-4 flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><MoonStar size={18} /></span>
        <div className="flex-1 min-w-0">
          <h2 className="font-display font-bold text-slate-900 text-sm">Night Audit</h2>
          <p className="text-[11px] text-slate-500 mt-0.5">{propertyName} · advances the Operating Day when every guest is fully paid.</p>
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Real-world date</p>
          <p className="font-mono text-sm font-bold text-slate-900">{d.business_today}</p>
        </div>
      </div>

      <section className={`rounded-2xl border-2 p-6 ${held ? "border-amber-300 bg-amber-50/40" : "border-indigo-200 bg-white"}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Current Operating Day</p>
            <p className="font-display text-3xl font-bold text-slate-900 mt-1" data-testid="na-current-day">{c.operating_day}</p>
            <p className={`text-xs font-semibold mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full ${
              held ? "bg-amber-100 text-amber-800 border border-amber-300"
                   : c.status === "completed" ? "bg-emerald-100 text-emerald-800"
                   : "bg-indigo-100 text-indigo-800"
            }`} data-testid="na-status">
              {held ? <AlertTriangle size={12} /> : c.status === "completed" ? <CheckCircle2 size={12} /> : <CalendarCheck2 size={12} />}
              {held ? "Held · unpaid guests still in-house" : c.status === "completed" ? "Completed" : (isBehind ? "Pending · past cutoff" : "Pending · today")}
            </p>
          </div>
          {canRun && (
            <button
              data-testid="na-run"
              onClick={() => runAudit(false)}
              disabled={busy}
              className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-full px-5 py-2.5 flex items-center gap-2 disabled:opacity-50"
            >
              {busy ? <Loader2 className="animate-spin" size={13} /> : <PlayCircle size={14} />}
              Run Night Audit
            </button>
          )}
        </div>

        {blockers.length > 0 && (
          <div className="mt-5">
            <p className="text-[10px] uppercase font-semibold tracking-wider text-amber-800 mb-2">
              {blockers.length} outstanding payment{blockers.length > 1 ? "s" : ""} blocking auto-close
            </p>
            <table className="w-full text-xs bg-white rounded-lg border border-amber-200 overflow-hidden">
              <thead className="bg-amber-100/60">
                <tr className="text-left text-[10px] uppercase tracking-wider text-amber-900">
                  <th className="px-3 py-2 font-semibold">Guest</th>
                  <th className="px-3 py-2 font-semibold">Unit</th>
                  <th className="px-3 py-2 text-right font-semibold">Total ₹</th>
                  <th className="px-3 py-2 text-right font-semibold">Paid ₹</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance ₹</th>
                </tr>
              </thead>
              <tbody>
                {blockers.map((b) => (
                  <tr key={b.booking_id} className="border-t border-amber-100">
                    <td className="px-3 py-2 font-semibold text-slate-900">{b.guest}</td>
                    <td className="px-3 py-2">{b.unit_name}</td>
                    <td className="px-3 py-2 text-right font-mono">{Number(b.amount).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono text-emerald-700">{Number(b.paid).toLocaleString("en-IN")}</td>
                    <td className="px-3 py-2 text-right font-mono text-amber-800 font-bold">{Number(b.balance_due).toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[11px] text-amber-800 mt-2">Collect these payments and re-run, or use <b>Force close</b> below to close the day with the balance still open.</p>
            {forceOpen && (
              <button data-testid="na-force" onClick={() => runAudit(true, "Manually forced with unpaid balance")}
                disabled={busy} className="mt-3 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-full px-4 py-2 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin inline mr-1" size={12} /> : null}
                Force close anyway
              </button>
            )}
          </div>
        )}
        {blockers.length === 0 && c.status === "pending" && (
          <p className="text-[11px] text-slate-500 mt-4">
            {isBehind
              ? "This day is past the cutoff and has no unpaid guests — clicking Run Night Audit will close it and open the next Operating Day."
              : "You can run this manually any time. If you leave it, the system will auto-run at cutoff (03:00 tomorrow) provided all balances are cleared."}
          </p>
        )}
      </section>

      {pending && pending.count > 0 && (
        <section data-testid="na-month-pending" className="rounded-2xl border-2 border-rose-200 bg-gradient-to-br from-rose-50 via-white to-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <span className="h-11 w-11 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center shrink-0">
                <IndianRupee size={20} />
              </span>
              <div>
                <p className="text-[10px] uppercase font-bold tracking-wider text-rose-700">Pending this month · month-to-date reminder</p>
                <h3 className="font-display font-bold text-slate-900 text-lg mt-0.5">
                  {pending.count} guest{pending.count > 1 ? "s owe" : " owes"} ₹{Number(pending.total_due).toLocaleString("en-IN")}
                </h3>
                <p className="text-[11px] text-slate-500 mt-1">
                  Every stay from <b>{fmtMonth(pending.month_start)}</b> with an open balance — stays visible even after Night Audit moves on. Collect these to close the month cleanly.
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase font-semibold tracking-wider text-slate-400">Collected MTD</p>
              <p className="font-mono text-sm font-bold text-emerald-600">₹{Number(pending.total_collected_this_month).toLocaleString("en-IN")}</p>
            </div>
          </div>
          <div className="mt-5 -mx-6 md:mx-0 overflow-x-auto overscroll-contain">
            <table className="w-full text-xs min-w-[720px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-200 bg-slate-50/60">
                  <th className="px-4 py-2.5 font-semibold">Guest · Unit</th>
                  <th className="px-4 py-2.5 font-semibold">Stay</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Total ₹</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Paid ₹</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Balance ₹</th>
                  <th className="px-4 py-2.5 text-right font-semibold">Overdue</th>
                </tr>
              </thead>
              <tbody>
                {pending.pending.map((p) => (
                  <tr key={p.booking_id} data-testid={`na-pending-row-${p.booking_id}`} className="border-b border-slate-100 hover:bg-rose-50/40 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900 flex items-center gap-1.5"><User size={12} className="text-slate-400" /> {p.guest || "Guest"}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{p.unit_name} · {p.listing_title || "—"}</p>
                    </td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-600">
                      {p.checkin} → {p.checkout}
                      <span className="ml-1 text-slate-400">({p.nights}n)</span>
                    </td>
                    <td className="px-4 py-3 text-right font-mono text-slate-700">{Number(p.amount).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono text-emerald-600">{Number(p.paid).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right font-mono text-rose-700 font-bold">{Number(p.balance_due).toLocaleString("en-IN")}</td>
                    <td className="px-4 py-3 text-right">
                      {p.days_overdue > 0 ? (
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${p.days_overdue >= 7 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"}`}>
                          <Clock size={9} /> {p.days_overdue}d
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-400">on time</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-slate-400 mt-3">Tip · rows are sorted worst-first (most overdue, biggest balance). Collect + refresh to see the list shrink in real time.</p>
        </section>
      )}
      {pending && pending.count === 0 && (
        <section data-testid="na-month-clean" className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-white p-5 flex items-center gap-3">
          <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-600 flex items-center justify-center shrink-0"><CheckCircle2 size={18} /></span>
          <div>
            <p className="font-display font-bold text-slate-900 text-sm">All caught up for {fmtMonth(pending.month_start)}</p>
            <p className="text-[11px] text-slate-500 mt-0.5">Every stay this month is fully paid. Nothing pending to remind you about.</p>
          </div>
        </section>
      )}

      {d.history.length > 0 && (
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 text-sm mb-3 flex items-center gap-2"><History size={14} /> Recent Operating Days</h3>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left py-2 font-semibold">Day</th>
                <th className="text-left font-semibold">Closed at</th>
                <th className="text-left font-semibold">Notes</th>
              </tr>
            </thead>
            <tbody>
              {d.history.map((h) => (
                <tr key={h.operating_day} className="border-b border-slate-50">
                  <td className="py-2.5 font-mono font-semibold text-slate-900">{h.operating_day}</td>
                  <td className="py-2.5 font-mono text-slate-500">{(h.closed_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2.5 text-slate-600">{h.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
